import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCog, FaSyncAlt, FaSpinner } from 'react-icons/fa';
import Header from "../../../header/header.js";
import Navvbar from "../../../header/Navvbar.js";
import AddFile from "./Addfile.js";
import "./CaseFiles.css";

// ============================================================
// Status helpers — mirror backend NOT_ON_HOLD_STATUSES
// ============================================================
const NOT_ON_HOLD_STATUSES = [
  'not on hold',
  'in progress',
  'completed',
  'delivered',
  'cancelled',
  'canceled',
  'outsourced',
  'preinvoiced',
  'pre-invoiced',
  'invoiced',
  'shipped',
  'ready',
  'ready to ship',
  'hold released',
  'release',
];

// Compute the display type for a status
const getDisplayStatus = (caseItem) => {
  const raw = (caseItem.status || '').toString().trim();
  if (!raw) return { label: '—', type: 'unknown' };

  const lower = raw.toLowerCase();

  if (
    lower === 'on hold' ||
    lower === 'hold case' ||
    lower.includes('hold') ||
    lower.includes('try-in') ||
    lower.includes('die trim') ||
    lower.includes('problem with case')
  ) {
    return { label: raw, type: 'hold' };
  }
  if (NOT_ON_HOLD_STATUSES.includes(lower)) {
    return { label: raw, type: 'normal' };
  }
  return { label: raw, type: 'unknown' };
};

// Safe renderer for addedBy — handles both string and object shapes
const renderAddedBy = (addedBy) => {
  if (!addedBy) return '—';
  if (typeof addedBy === 'string') return addedBy;
  if (typeof addedBy === 'object') {
    const labId = addedBy.userLabId || '';
    const currentLabId = localStorage.getItem('userLabId') || '';
    if (labId && currentLabId && labId === currentLabId) return 'Lab';
    return labId ? `Lab (${labId})` : 'Lab';
  }
  return '—';
};

// Safe renderer for procedures array
const renderProcedures = (procedures) => {
  if (!Array.isArray(procedures)) return '';
  return procedures.map((proc) => proc.procedure).join('; ');
};

const CaseFiles = () => {
  const [showParameters, setShowParameters] = useState(false);
  const [filteredCases, setFilteredCases] = useState([]);
  const [filters, setFilters] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('filterData')) || {};
    } catch (error) {
      console.error('Error parsing filter data:', error);
      return {};
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const containerRef = useRef(null);
  const tableRef = useRef(null);

  // -----------------------------------------------------------
  // Fetch filtered cases from dedicated endpoint
  // -----------------------------------------------------------
  const fetchFilteredCases = async (filterParams) => {
    if (!filterParams.addedFrom || !filterParams.addedTo) {
      setFilteredCases([]);
      return;
    }

    const userLabId = localStorage.getItem('userLabId');
    if (!userLabId) {
      setError('userLabId missing in localStorage. Please login again.');
      setFilteredCases([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const queryParams = new URLSearchParams();

      // Required
      queryParams.append('userLabId', userLabId);
      queryParams.append('addedFrom', filterParams.addedFrom);
      queryParams.append('addedTo', filterParams.addedTo);

      // Optional
      if (filterParams.addedBy) queryParams.append('addedBy', filterParams.addedBy);
      if (filterParams.selectedAccount) queryParams.append('selectedAccount', filterParams.selectedAccount);
      if (filterParams.accountTags) queryParams.append('accountTags', filterParams.accountTags);
      if (filterParams.caseTags) queryParams.append('caseTags', filterParams.caseTags);
      if (filterParams.procedure && filterParams.procedure !== 'All') {
        queryParams.append('procedure', filterParams.procedure);
      }
      if (filterParams.caseStatus) queryParams.append('caseStatus', filterParams.caseStatus);

      // Flags
      queryParams.append('includeUnarrived', String(!!filterParams.includeUnarrived));
      queryParams.append('includeOnHold', String(!!filterParams.includeOnHold));
      queryParams.append('includeClosedCases', String(!!filterParams.includeClosedCases));
      queryParams.append('includeClosed', String(!!filterParams.includeClosed));

      const url = `https://hms.backend.hiragroupcloud.com/api/cases/filter-cases?${queryParams.toString()}`;
      console.log('📡 Fetching filtered cases:', url);

      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} ${response.statusText}. ${errorText}`);
      }

      const json = await response.json();
      setFilteredCases(json.data || []);
    } catch (err) {
      console.error('❌ Error fetching filtered cases:', err);
      setError('Error fetching filtered cases: ' + err.message);
      setFilteredCases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFilteredCases(filters);
  }, [filters]);

  // Auto-scale the table
  useLayoutEffect(() => {
    const applyScale = () => {
      const container = containerRef.current;
      const table = tableRef.current;
      if (!container || !table) return;

      table.style.transform = 'scale(1)';
      table.style.width = '100%';

      const containerWidth = container.clientWidth;
      const tableWidth = table.scrollWidth;

      if (tableWidth > containerWidth && tableWidth > 0) {
        const scale = containerWidth / tableWidth;
        table.style.transform = `scale(${scale})`;
        table.style.transformOrigin = 'top left';
        container.style.height = `${table.offsetHeight * scale}px`;
      } else {
        table.style.transform = 'scale(1)';
        container.style.height = 'auto';
      }
    };

    applyScale();

    const resizeObserver = new ResizeObserver(() => applyScale());
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    window.addEventListener('resize', applyScale);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', applyScale);
    };
  }, [filteredCases, loading]);

  const handleRunFilters = (incomingFilterData) => {
    try {
      const newFilterData =
        incomingFilterData ||
        JSON.parse(localStorage.getItem('filterData')) ||
        {};
      setFilters(newFilterData);
      setShowParameters(false);
    } catch (error) {
      console.error('Error parsing filter data:', error);
      setError('Invalid filter data');
    }
  };

  const retryFetch = () => {
    fetchFilteredCases(filters);
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).replace(',', ' at');
  };

  const handleEdit = (account) => {
    navigate(`/TabsWorking/${account.accountId}`, {
      state: {
        accountName: account.account_name,
        accountData: account,
      },
    });
  };

  return (
    <>
      <Header />
      <Navvbar />
      <div className="casefiles-wrapper">
        <div className="casefiles-header">
          <h2 className="casefiles-title">Case Files</h2>
          <div>
            <button
              className="casefiles-parameters-btn"
              onClick={() => setShowParameters(!showParameters)}
            >
              <FaCog /> Parameters
            </button>
          </div>
        </div>

        {showParameters && (
          <div className="casefiles-parameters-wrapper">
            <AddFile onRun={handleRunFilters} />
          </div>
        )}

        {error && (
          <div className="casefiles-error">
            <div className="casefiles-error-message">{error}</div>
            <button className="casefiles-retry-btn" onClick={retryFetch}>
              <FaSyncAlt /> Retry
            </button>
          </div>
        )}

        {loading ? (
          <div className="casefiles-loading">
            <FaSpinner className="spin" /> Loading filtered cases...
          </div>
        ) : (
          <div className="casefiles-table-container" ref={containerRef}>
            <table className="casefiles-table" ref={tableRef}>
              <thead>
                <tr>
                  <th>Case</th>
                  <th>Status</th>
                  <th>Case Submission Time</th>
                  <th>Account</th>
                  <th>Provider Name</th>
                  <th>Patient</th>
                  <th>Procedure</th>
                  <th>File</th>
                  <th>Added By</th>
                  <th>Added</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="casefiles-empty">
                      {filters.addedFrom && filters.addedTo
                        ? 'No cases found matching your filters. Try adjusting your criteria.'
                        : 'Please set date range filters to see cases.'}
                    </td>
                  </tr>
                ) : (
                  filteredCases.map((caseItem) => {
                    const display = getDisplayStatus(caseItem);
                    return (
                      <tr
                        key={`${caseItem._id}-${caseItem.fileName || ''}`}
                        className="casefiles-row"
                      >
                        <td>
                          <a
                            href={`/case/${caseItem.case_id}`}
                            className="casefiles-case-link"
                          >
                            {caseItem.case_id}
                          </a>
                        </td>
                        <td>
                          <span
                            className={`casefiles-status-badge casefiles-status-${display.type}`}
                            title={display.label}
                          >
                            {display.label}
                          </span>
                        </td>
                        <td>{formatDateTime(caseItem.createdAt)}</td>
                        <td>
                          <strong
                            className="casefiles-account-name"
                            onClick={() => handleEdit(caseItem)}
                          >
                            {caseItem.account_name}
                          </strong>
                          <br />
                          <small className="casefiles-account-id">
                            ID: {caseItem.accountId}
                          </small>
                        </td>
                        <td>{caseItem.provider_name}</td>
                        <td>
                          <strong>
                            {`${caseItem.first_name || ''} ${caseItem.last_name || ''}`.trim() || '—'}
                          </strong>
                        </td>
                        <td>{renderProcedures(caseItem.procedures)}</td>
                        <td>{caseItem.fileName || '—'}</td>
                        <td>{renderAddedBy(caseItem.addedBy)}</td>
                        <td>{formatDateTime(caseItem.createdAt)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default CaseFiles;